import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, RefreshCw, FlaskConical } from "lucide-react";
import { logError } from "@/lib/errorLogger";

type Row = {
  id: string;
  fingerprint: string;
  route: string | null;
  error_class: string;
  status_code: number | null;
  message: string | null;
  stack_signature: string | null;
  severity: "normal" | "high" | "critical";
  occurrence_count: number;
  first_seen: string;
  last_seen: string;
  status: "new" | "acknowledged" | "diagnosed" | "fixed" | "regressed";
  resolution_notes: string | null;
  context: Record<string, unknown>;
};

const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, normal: 2 };

export default function ErrorRegister() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [fixDialogRow, setFixDialogRow] = useState<Row | null>(null);
  const [fixNotes, setFixNotes] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("error_events")
      .select("*")
      .order("last_seen", { ascending: false })
      .limit(500);
    if (error) {
      toast.error(error.message);
      setRows([]);
    } else {
      setRows((data || []) as Row[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return rows
      .filter((r) => severityFilter === "all" || r.severity === severityFilter)
      .filter((r) => statusFilter === "all" || r.status === statusFilter)
      .sort((a, b) => {
        const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
        if (s !== 0) return s;
        return new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime();
      });
  }, [rows, severityFilter, statusFilter]);

  const setStatus = async (row: Row, next: Row["status"], notes?: string) => {
    if (next === "fixed" && !notes?.trim()) {
      toast.error("Resolution notes are required to mark fixed");
      return;
    }
    const patch: any = { status: next };
    if (notes !== undefined) patch.resolution_notes = notes || null;
    const { error } = await (supabase as any)
      .from("error_events")
      .update(patch)
      .eq("id", row.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`Marked ${next}`);
      setFixDialogRow(null);
      setFixNotes("");
      load();
    }
  };

  const runSelfTest = async () => {
    const id = await logError({
      errorClass: "self_test",
      route: "/admin/system/errors",
      message: `Self-test event at ${new Date().toISOString()}`,
      context: { triggered_by: "admin_self_test" },
    });
    if (id) toast.success("Test event logged");
    else toast.message("Test event queued (may be debounced)");
    setTimeout(load, 800);
  };

  const sevBadge = (s: string) => {
    const map: Record<string, string> = {
      critical: "bg-red-100 text-red-800 border-red-300",
      high: "bg-orange-100 text-orange-800 border-orange-300",
      normal: "bg-slate-100 text-slate-700 border-slate-300",
    };
    return <Badge variant="outline" className={map[s]}>{s}</Badge>;
  };

  const statusBadge = (s: string) => {
    const map: Record<string, string> = {
      new: "bg-blue-100 text-blue-800 border-blue-300",
      acknowledged: "bg-amber-100 text-amber-800 border-amber-300",
      diagnosed: "bg-indigo-100 text-indigo-800 border-indigo-300",
      fixed: "bg-green-100 text-green-800 border-green-300",
      regressed: "bg-red-100 text-red-800 border-red-300",
    };
    return <Badge variant="outline" className={map[s]}>{s}</Badge>;
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "Georgia, serif" }}>
            Error Register
          </h1>
          <p className="text-sm text-muted-foreground">
            Every logged error, grouped by fingerprint. Sorted by severity then most recent.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          {/* Hidden-in-plain-sight self-test */}
          <Button variant="outline" size="sm" onClick={runSelfTest} title="Log a synthetic normal-severity event">
            <FlaskConical className="w-4 h-4 mr-1" />
            Log test event
          </Button>
        </div>
      </div>

      <Card className="border-gold/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Severity</span>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Status</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="diagnosed">Diagnosed</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="regressed">Regressed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Route</TableHead>
                <TableHead className="text-right">Count</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">No errors match the current filters.</TableCell></TableRow>
              )}
              {filtered.map((r) => {
                const open = expanded === r.id;
                return (
                  <>
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => setExpanded(open ? null : r.id)}>
                      <TableCell>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                      <TableCell>{sevBadge(r.severity)}</TableCell>
                      <TableCell className="font-mono text-xs">{r.error_class}</TableCell>
                      <TableCell className="max-w-xs truncate text-xs">{r.route || "—"}</TableCell>
                      <TableCell className="text-right">{r.occurrence_count}</TableCell>
                      <TableCell>{statusBadge(r.status)}</TableCell>
                      <TableCell className="text-xs">{new Date(r.last_seen).toLocaleString()}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1 flex-wrap">
                          {r.status === "new" && (
                            <Button size="sm" variant="outline" onClick={() => setStatus(r, "acknowledged")}>Ack</Button>
                          )}
                          {(r.status === "acknowledged" || r.status === "new") && (
                            <Button size="sm" variant="outline" onClick={() => setStatus(r, "diagnosed")}>Diagnosed</Button>
                          )}
                          {r.status !== "fixed" && (
                            <Button size="sm" onClick={() => { setFixDialogRow(r); setFixNotes(r.resolution_notes || ""); }}>
                              Mark fixed
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow key={r.id + "-detail"}>
                        <TableCell colSpan={8} className="bg-muted/30">
                          <div className="text-xs space-y-2 py-2">
                            <div><span className="font-semibold">Fingerprint:</span> <code>{r.fingerprint}</code></div>
                            <div><span className="font-semibold">Status code:</span> {r.status_code ?? "—"}</div>
                            <div><span className="font-semibold">First seen:</span> {new Date(r.first_seen).toLocaleString()}</div>
                            <div>
                              <span className="font-semibold">Message:</span>
                              <pre className="whitespace-pre-wrap bg-background p-2 rounded mt-1">{r.message || "—"}</pre>
                            </div>
                            <div>
                              <span className="font-semibold">Stack signature:</span>
                              <pre className="whitespace-pre-wrap bg-background p-2 rounded mt-1">{r.stack_signature || "—"}</pre>
                            </div>
                            <div>
                              <span className="font-semibold">Context:</span>
                              <pre className="whitespace-pre-wrap bg-background p-2 rounded mt-1">{JSON.stringify(r.context || {}, null, 2)}</pre>
                            </div>
                            {r.resolution_notes && (
                              <div>
                                <span className="font-semibold">Resolution notes:</span>
                                <pre className="whitespace-pre-wrap bg-background p-2 rounded mt-1">{r.resolution_notes}</pre>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!fixDialogRow} onOpenChange={(o) => { if (!o) { setFixDialogRow(null); setFixNotes(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as fixed</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Resolution notes are required. If this fingerprint fires again it will be marked <b>regressed</b>.
            </p>
            <Textarea
              value={fixNotes}
              onChange={(e) => setFixNotes(e.target.value)}
              rows={4}
              placeholder="What was the fix?"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setFixDialogRow(null); setFixNotes(""); }}>Cancel</Button>
            <Button onClick={() => fixDialogRow && setStatus(fixDialogRow, "fixed", fixNotes)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
